import {
  TransformWrapper,
  TransformComponent,
  ReactZoomPanPinchRef,
} from "react-zoom-pan-pinch";
import _ from "lodash";
import { Repository } from "../types/repository";

import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Paper } from "../types/Paper";
import { Keyword } from "../types/Keyword";
import RepoSideBar from "../components/RepoSideBar/RepoSideBar";
import { Parameter } from "../types/Parameter";
import PaperComponent from "../components/Paper/Paper";

export interface SortOptions {
  type: "parameters" | "date";
  parameterKey: string;
  order: "increasing" | "decreasing";
}

const calculateBackgroundColor = (min: number, max: number, value: number) => {
  // Check if min, max, and value are the same
  if (max === min && min === value) {
    if (value >= 0) {
      return "hsl(120, 100%, 50%)"; // Middle green for non-negative identical values
    } else {
      return "hsl(0, 100%, 50%)"; // Middle red for negative identical values
    }
  }

  let normalizedValue;
  if (value >= 0) {
    // For positive values, normalize based on the range from 0 to max
    normalizedValue = max !== 0 ? value / max : 1; // Avoid division by zero if max is 0
    // Map to green spectrum (from light green to dark green)
    const lightness = 100 - normalizedValue * 50; // Lightness from 100% to 50%
    return `hsl(120, 100%, ${lightness}%)`; // Green with varying lightness
  } else {
    // For negative values, normalize based on the range from min to 0
    normalizedValue = min !== 0 ? value / min : 1; // Avoid division by zero if min is 0
    // Map to red spectrum (from light red to dark red)
    const lightness = 100 - Math.abs(normalizedValue) * 50; // Lightness from 100% to 50%
    return `hsl(0, 100%, ${lightness}%)`; // Red with varying lightness
  }
};

const Visualization = () => {
  const location = useLocation();
  const { repository } = location.state as { repository: Repository };
  const repositoryId = repository.id;
  const [keywordPapersMap, setKeywordPapersMap] = useState<{
    [key: string]: {
      papers: Paper[];
      paramStats: { [paramKey: string]: { min: number; max: number } };
    };
  }>({});

  const [hoveredKeyword, setHoveredKeyword] = useState<Keyword | null>(null);
  const [hoveredParameter, setHoveredParameter] = useState<Parameter | null>(
    null
  );

  const [selectedParameter, setSelectedParameter] = useState<Parameter | null>(
    null
  );

  const [selectedPaper, setSelectedPaper] = useState<Paper | null>(null);

  const handleParameterClick = (parameter: Parameter) => {
    if (selectedParameter && selectedParameter.key === parameter.key) {
      // If the clicked parameter is already selected, deselect it
      setSelectedParameter(null);
    } else {
      // Otherwise, select the new parameter
      setSelectedParameter(parameter);
      resetZoom();
    }
  };

  const handleNavBarPaperClick = (paper: Paper) => {
    console.log("paper clicked", paper);
    if (selectedPaper && selectedPaper.id === paper.id) {
      console.log("deselecting paper");
      // If the clicked parameter is already selected, deselect it
      setSelectedPaper(null);
    } else {
      // Otherwise, select the new parameter
      console.log("selecting paper");
      setSelectedPaper(paper);
      resetZoom();
    }
  };

  const doesPaperHaveHoveredOrSelectedParameter = (paper: Paper) => {
    const isParameterHoveredOn =
      hoveredParameter &&
      isParameterInPaper(paper.parameters || [], hoveredParameter.key);
    const isParameterSelected =
      selectedParameter &&
      isParameterInPaper(paper.parameters || [], selectedParameter.key);
    return isParameterHoveredOn || isParameterSelected;
  };

  const isPaperSelectedFromNavBar = (paper: Paper) => {
    if (paper.id === selectedPaper?.id) {
      console.log("paper is selected", paper);
    }
    return paper.id === selectedPaper?.id;
  };

  const papers = useMemo(() => {
    return repository.papers || [];
  }, [repository]);
  const allRepositoryParameters = useMemo(() => {
    const allParams: Parameter[] = [];
    papers.forEach((paper) => {
      const paperParameters: Parameter[] = paper.parameters || [];
      allParams.push(...paperParameters);
    });
    return allParams;
  }, [papers]);

  const repositoryKeywords = useMemo(() => {
    const allKeywords: Keyword[] = [];
    papers.forEach((paper) => {
      const paperKeywords: Keyword[] = paper.keywords || [];
      allKeywords.push(...paperKeywords);
    });
    return allKeywords;
  }, [papers]);

  const uniqueRepositoryParameters = useMemo(
    () => _.uniqBy(allRepositoryParameters, "key"),
    [allRepositoryParameters]
  );

  const uniqueRepositoryKeywords = useMemo(
    () => _.uniqBy(repositoryKeywords, "keyword"),
    [repositoryKeywords]
  );

  useEffect(() => {
    const keywordsToPaperDataMap: {
      [keyword: string]: {
        papers: Paper[];
        paramStats: { [paramKey: string]: { min: number; max: number } };
      };
    } = {};

    for (const paper of papers) {
      const paperKeywords = paper.keywords || [];
      const paperParameters: Parameter[] = paper.parameters || [];

      paperKeywords.forEach((keywordObj: Keyword) => {
        const keywordName = keywordObj.keyword;
        if (!keywordsToPaperDataMap[keywordName]) {
          keywordsToPaperDataMap[keywordName] = {
            papers: [],
            paramStats: {},
          };
        }
        keywordsToPaperDataMap[keywordName].papers.push(paper);

        paperParameters.forEach((param) => {
          const stats = keywordsToPaperDataMap[keywordName].paramStats[
            param.key
          ] || { min: Infinity, max: -Infinity };
          stats.min = Math.min(stats.min, Number(param.value));
          stats.max = Math.max(stats.max, Number(param.value));
          keywordsToPaperDataMap[keywordName].paramStats[param.key] = stats;
        });
      });
    }

    setKeywordPapersMap(keywordsToPaperDataMap);
  }, [papers]);

  const sortPapers = ({ type, parameterKey, order }: SortOptions) => {
    const keywordPapersMapSortedByParamStats = _.mapValues(
      sortedKeywordPapersMap,
      ({ papers, paramStats }) => {
        switch (type) {
          case "parameters":
            // Sorting by parameter
            const sortedPapers = _.orderBy(
              papers,
              [
                (paper) => {
                  const params = paper.parameters || [];
                  const param = _.find(params, { key: parameterKey });
                  return param
                    ? param.value
                    : order === "increasing"
                      ? Number.POSITIVE_INFINITY
                      : Number.NEGATIVE_INFINITY;
                },
              ],
              [order === "increasing" ? "asc" : "desc"]
            );
            return {
              papers: sortedPapers, // Sorted papers
              paramStats, // Return existing paramStats unchanged
            };
          case "date":
            return {
              papers,
              paramStats,
            };
          default:
            return {
              papers,
              paramStats,
            }; // Returns unsorted if type is not recognized
        }
      }
    );

    setKeywordPapersMap(keywordPapersMapSortedByParamStats);
  };

  const sortedKeywordPapersMap = useMemo(() => {
    // Convert keywordPapersMap to an array of [keyword, data] entries
    const keywordEntries = Object.entries(keywordPapersMap);

    // Sort the entries by the length of papers in descending order
    const sortedEntries = _.orderBy(
      keywordEntries,
      ([, data]) => data.papers.length,
      "desc"
    );

    // Convert the sorted entries back into an object
    return _.fromPairs(sortedEntries) as {
      [key: string]: {
        papers: Paper[];
        paramStats: { [paramKey: string]: { min: number; max: number } };
      };
    };
  }, [keywordPapersMap]);

  const sortedUniqueRepositoryKeywords = useMemo(() => {
    // Create a mapping of keys to their index positions
    const keyOrder = _.fromPairs(
      _.keys(sortedKeywordPapersMap).map((key, index) => [key, index])
    ) as { [key: string]: number };

    // Sort uniqueRepositoryKeywords based on the order in sortedKeywordPapersMap
    return _.sortBy(
      uniqueRepositoryKeywords,
      (keyword: Keyword) => keyOrder[keyword.keyword]
    );
  }, [sortedKeywordPapersMap, uniqueRepositoryKeywords]);
  const isParameterInPaper = (
    paperParameters: Parameter[],
    parameterKey: string
  ) => {
    return _.some(paperParameters, { key: parameterKey });
  };

  const getParameterInPaper = (
    paperParameters: Parameter[],
    parameterKey?: string
  ) => {
    if (!parameterKey) return null;
    return _.find(paperParameters, { key: parameterKey });
  };

  const handleKeywordClick = (keyword: Keyword) => {
    const keywordElement: HTMLElement | null = document.querySelector(
      `[data-keyword="${keyword.keyword}"]`
    );
    resetZoom();
    if (keywordElement) {
      centerKeyword(keywordElement);
    }
  };

  const centerKeyword = (keywordElement: HTMLElement) => {
    keywordElement.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  };

  const transformComponentRef = useRef<ReactZoomPanPinchRef | null>(null);
  const [initialScale, setInitialScale] = useState(0.16);

  useEffect(() => {
    const calculateInitialScaleOfTree = () => {
      const scaleX = 1 / (_.keys(keywordPapersMap).length + 1);
      setInitialScale(scaleX);
      if (transformComponentRef.current) {
        transformComponentRef.current.setTransform(0, 0, scaleX, 0, "easeOut"); // Reset to initialScale
      }
    };

    calculateInitialScaleOfTree();
  }, [keywordPapersMap]); // Only depend on keywordPapersMap

  const resetZoom = () => {
    if (transformComponentRef.current) {
      transformComponentRef.current.setTransform(
        0,
        0,
        initialScale,
        300,
        "easeOut"
      ); // Reset to initialScale
    }
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <nav className="col-md-3 d-none d-md-block sidebar details">
          <div className="sidebar-sticky">
            <div className="navbar-collapse" id="navbarNavDropdown">
              <ul className="navbar-nav">
                <li className="nav-item dropdown">
                  <a
                    className="nav-link"
                    href="/#"
                    onClick={(event) => {
                      event.preventDefault();
                    }}
                  >
                    {repository?.name}
                  </a>
                  <ul className="dropdown-menu d-block">
                    {papers.map((paper) => (
                      <li key={paper.id}>
                        <a
                          className="dropdown-item"
                          href="/#"
                          onClick={(event) => {
                            event.preventDefault();
                            handleNavBarPaperClick(paper);
                          }}
                        >
                          {paper.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </li>
              </ul>
            </div>
          </div>
        </nav>

        <main
          role="main"
          className="workspace main zoom-sec col-md-9 ml-sm-auto col-lg-6 pt-3 px-4"
        >
          {/* <div className="top-bar">
            <div>
              <button className="btn active">
                Key Phrase Based visualization
              </button>
            </div>

            <div className="dropdown">
              <a
                aria-expanded="false"
                aria-haspopup="true"
                role="button"
                data-toggle="dropdown"
                className="dropdown-toggle"
                href="/#"
                onClick={(event) => {
                  event.preventDefault();
                }}
              >
                <span id="selected">Visualization mode</span>
                <span className="caret"></span>
              </a>
              <ul className="dropdown-menu" id="topbar-dropdown">
                <li>Visualization mode</li>
              </ul>
            </div>
          </div> */}

          <div className="section workspace-content" id="zoom-space">
            <TransformWrapper
              // key={initialScale}
              initialScale={initialScale}
              limitToBounds={false}
              minScale={0.001}
              maxScale={3}
              centerZoomedOut={false}
              centerOnInit={false}
              disablePadding={true}
              smooth={true}
              ref={transformComponentRef}
            >
              <TransformComponent>
                <div className="tree">
                  <ul className="actual-tree">
                    <li>
                      <a
                        href="/#"
                        className="top keywords-tree"
                        onClick={(event) => {
                          event.preventDefault();
                        }}
                      >
                        Keywords Tree
                      </a>
                      <ul>
                        <li>
                          <ul className="keywords-tree">
                            {_.map(sortedKeywordPapersMap, (data, keyword) => (
                              <li key={keyword} data-keyword={keyword}>
                                <a
                                  href="/#"
                                  className="first"
                                  onClick={(event) => {
                                    event.preventDefault();
                                  }}
                                >
                                  {keyword}
                                </a>
                                {data.papers.map((paper) => {
                                  const isKeywordActive =
                                    hoveredKeyword?.keyword === keyword;
                                  const isParameterActive =
                                    doesPaperHaveHoveredOrSelectedParameter(
                                      paper
                                    );
                                  const isPaperActive =
                                    isPaperSelectedFromNavBar(paper);
                                  const isActive =
                                    isKeywordActive ||
                                    isParameterActive ||
                                    isPaperActive;

                                  var param = getParameterInPaper(
                                    paper.parameters || [],
                                    hoveredParameter?.key
                                  );
                                  if (!param) {
                                    param = getParameterInPaper(
                                      paper.parameters || [],
                                      selectedParameter?.key
                                    );
                                  }
                                  const paramValue = param?.value;
                                  const paramStats = param
                                    ? data.paramStats[param?.key]
                                    : null;
                                  let backgroundStyle = {};
                                  if (
                                    isActive &&
                                    paramValue !== undefined &&
                                    paramStats
                                  ) {
                                    const backgroundColor =
                                      calculateBackgroundColor(
                                        paramStats.min,
                                        paramStats.max,
                                        Number(paramValue)
                                      );
                                    backgroundStyle = {
                                      backgroundColor: backgroundColor,
                                    };
                                  }
                                  return (
                                    <ul>
                                      <li
                                        className={
                                          isKeywordActive ? `k-active` : ""
                                        }
                                        key={`paper_${paper.id}`}
                                        style={backgroundStyle}
                                      >
                                        <ul>
                                          <a
                                            href="/#"
                                            onClick={(event) => {
                                              event.preventDefault();
                                            }}
                                          >
                                            {isActive && (
                                              <span className="hovered-parameter-value">
                                                {
                                                  _.find(paper.parameters, {
                                                    key: param?.key,
                                                  })?.value
                                                }
                                              </span>
                                            )}
                                            <PaperComponent
                                              paper={paper}
                                              repositoryId={
                                                repositoryId ? repositoryId : ""
                                              }
                                            />
                                          </a>
                                        </ul>
                                      </li>
                                    </ul>
                                  );
                                })}
                              </li>
                            ))}
                          </ul>
                        </li>
                      </ul>
                    </li>
                  </ul>
                </div>
              </TransformComponent>
            </TransformWrapper>
          </div>
        </main>
        <RepoSideBar
          resetZoom={resetZoom}
          onSort={sortPapers}
          isVisualization={true}
          keywords={sortedUniqueRepositoryKeywords}
          parameters={uniqueRepositoryParameters}
          repoId={repositoryId}
          onKeywordHover={(keyword: Keyword | null) =>
            setHoveredKeyword(keyword)
          }
          onKeywordLeave={() => setHoveredKeyword(null)}
          onKeywordClick={handleKeywordClick}
          onParameterHover={(parameter: Parameter | null) =>
            setHoveredParameter(parameter)
          }
          onParameterLeave={() => setHoveredParameter(null)}
          onParameterClick={handleParameterClick}
          selectedParameter={selectedParameter}
        ></RepoSideBar>
      </div>
    </div>
  );
};

export default Visualization;
